import {
  useState, useRef, useEffect, useContext,
} from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  useLiveAgentSession, useDrawerPrefs, createActionId, getDefaultSession,
  shouldResetSession, reconcileSession, sessionRecord, drawerPrefsRecord, overlayPrefsRecord,
} from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit } from '../platform/events/lifecycle';
import { emitExternalEntryClick } from '../shared/analytics';
import { EXTERNAL_ENTRY } from '../shared/assistant-config';
import { FLAGS, OVERLAY_TEST_ARMS } from '../platform/config/flags';

// sui's xl breakpoint. The adaptive arm swaps the docked panel and the overlay here.
const LARGE_DESKTOP_QUERY = '(min-width: 1280px)';

// Any named, non-FAB origin is an external entry point. Type-checked because `onClose` handlers
// call handleClose with a DOM event.
const isExternalEntryOrigin = (origin) => (
  typeof origin === 'string' && origin !== '' && origin !== EXTERNAL_ENTRY.ORIGIN_FAB
);

/**
 * The Shell's eager half: the drawer state machine and FAB behavior, in the main chunk so both
 * work before the assistant content loads. See .claude/context/session-identity.md.
 *
 * @param {object} [options]
 * @param {boolean} [options.isOverlayAllowed=false] false gives the control arm, whatever the
 *   test arm flag says.
 */
export const useDrawerController = ({ isOverlayAllowed = false } = {}) => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  // Cross-READ only; liveAgent block is the sole writer. See .claude/context/session-identity.md
  const [liveAgent] = useLiveAgentSession();
  // update() merges onto the record's current value; neither writer can revert the other.
  const [drawerPrefs, updatePrefs] = useDrawerPrefs();
  // The placement inputs, read once here and passed down to the shell, so the drawer state
  // machine and the placement always agree. See .claude/context/drawer-layout.md ("Overlay test
  // arms").
  const armValue = useConfigService(FLAGS.overlayTestArm);
  const arm = isOverlayAllowed && Object.values(OVERLAY_TEST_ARMS).includes(armValue)
    ? armValue
    : OVERLAY_TEST_ARMS.control;
  // `lesser` is below 1024px and `current` names the band. The mobile drawer ends at 1024px in the
  // control arm and at 768px in the overlay arms.
  const { lesser: isBelowLg, current } = useBreakpoint('lg', { current: true });
  const isMobile = arm === OVERLAY_TEST_ARMS.control
    ? isBelowLg
    : isBelowLg && (current === 'unprefixed' || current === 'sm');
  // A media query, not useBreakpoint, so the swap at 1280px does not wait for a window drag to
  // pause. See .claude/context/drawer-layout.md ("Overlay test arms").
  const [isLargeDesktop, setIsLargeDesktop] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.(LARGE_DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia?.(LARGE_DESKTOP_QUERY);
    if (!query) return undefined;
    const onChange = () => setIsLargeDesktop(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  // Do not use this setter directly, use updateDrawerState instead.
  const [drawerState, setDrawerState] = useState('CLOSED');
  // Lazy-init so the channel is created once, not re-allocated (and leaked) on every render.
  const broadcastChannel = useRef(null);
  if (!broadcastChannel.current) {
    broadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
  }
  // Mini drawer is disabled for B2B customers and while escalated to an agent (design decision).
  const enableMiniDrawer = customer?.type !== 'b2b' && !liveAgent?.isEscalated;

  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading; keep drawer state in sync across app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false, origin = EXTERNAL_ENTRY.ORIGIN_FAB) => {
    const isExternalEntry = isExternalEntryOrigin(origin);

    if (isUserInteraction && isExternalEntry) emitExternalEntryClick(EXTERNAL_ENTRY.OPEN_LABEL, origin);

    updateDrawerState('OPEN');
    // Read the prior flag before the update, so the fab-click event still fires on the first
    // interaction of the session.
    const alreadyInteracted = drawerPrefsRecord.get()?.hasSessionInteractedWithFab;
    updatePrefs({
      hasSessionInteractedWithFab: alreadyInteracted || isUserInteraction
    });
    // Different cadences: every entry-point ask counts, fab-click is once per session.
    if (isUserInteraction && !isExternalEntry && !alreadyInteracted) {
      const { sessionId } = sessionRecord.get() ?? {};
      emit('magic-apron-assistant.fab-click', {
        actionID: createActionId(sessionId),
        sessionID: sessionId,
      });
    }
  };

  // Opens this tab directly and broadcasts to peers (deliberate; user-confirmed 2026-07-27).
  // See .claude/context/composition-topology.md and .claude/context/session-identity.md
  const openDrawer = () => {
    handleOpen(false);
    broadcastChannel.current.postMessage({ type: 'MAA_OPEN_DRAWER' });
  };

  // Closing must NOT announce `isReady: false` — see .claude/context/event-bus.md.
  // `origin` is type-checked (isExternalEntryOrigin) since this is also passed straight to a
  // click-event `onClose`.
  const handleClose = (origin = EXTERNAL_ENTRY.ORIGIN_FAB) => {
    if (isExternalEntryOrigin(origin)) emitExternalEntryClick(EXTERNAL_ENTRY.CLOSE_LABEL, origin);

    updateDrawerState('CLOSED');
    updatePrefs({ shouldAutoOpen: false });
    // A close sends the overlay back to its default size and corner on the next open. See
    // .claude/context/drawer-layout.md ("Overlay preferences").
    if (overlayPrefsRecord.get().geometry) overlayPrefsRecord.update({ geometry: null });
  };

  // A drag-to-open is a user interaction, so OPEN routes through handleOpen(true).
  const handleDrawerStateChange = (newDrawerState) => {
    if (newDrawerState === 'CLOSED') handleClose();
    if (newDrawerState === 'OPEN') handleOpen(true);
    if (newDrawerState === 'MINIMIZED') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
      updatePrefs({ shouldAutoOpen: true });
    }
  };

  // Host asks a question on the user's behalf → remember to auto-open next time.
  const markShouldAutoOpen = () => updatePrefs({ shouldAutoOpen: true });

  useEffect(() => {
    // Seed, reconcile, then maybe reset — mount-only. See .claude/context/session-identity.md
    sessionRecord.seed();

    const reconciled = reconcileSession(sessionRecord.get());
    if (reconciled?.sessionId && reconciled.sessionId !== sessionRecord.get()?.sessionId) {
      sessionRecord.set(reconciled);
    }

    const willReset = shouldResetSession(sessionRecord.get());
    const outgoingPrefs = drawerPrefsRecord.get();
    const shouldAutoOpen = willReset
      ? outgoingPrefs.shouldAutoOpen !== false
      : outgoingPrefs?.shouldAutoOpen;

    if (willReset) {
      // maa-live-agent is deliberately NOT reset here — single-writer rule, see live-agent.md
      sessionRecord.set(getDefaultSession());
      drawerPrefsRecord.set({
        shouldAutoOpen,
        hasSessionInteractedWithFab: false,
        pagesExpandedFABShownOn: [],
      });
    }

    if (shouldAutoOpen) {
      let autoOpenState = 'OPEN';
      if (isMobile) autoOpenState = enableMiniDrawer ? 'MINIMIZED' : 'CLOSED';
      updateDrawerState(autoOpenState);
    }

    // Handles only drawer traffic; product pods emit their own analytics (see session-identity.md).
    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      } else if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        // Cross-tab drawer state synchronization for the mobile app.
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    return () => {
      broadcastChannel.current.close();
    };
  }, []);

  useEffect(() => {
    emit('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, changing orientation can flip the layout between open and minimized.
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  return {
    drawerState,
    placement: { arm, isMobile, isLargeDesktop },
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  };
};
